import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Mail, Github, AlertCircle } from 'lucide-react';

const ApprovalPendingPage = () => {
  const { user, logout, requestAccess, checkAuth } = useAuth();
  const [requesting, setRequesting] = React.useState(false);

  const handleReRequest = async () => {
    setRequesting(true);
    try {
      await requestAccess();
      await checkAuth(); // Refresh user state
    } catch (e) {
      console.error("Failed to re-request access:", e);
    } finally {
      setRequesting(false);
    }
  };

  const isRejected = user?.is_approved === -1;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-4">
      <Card className="w-full max-w-2xl p-8 shadow-xl">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 ${isRejected ? 'bg-red-100' : 'bg-yellow-100'} rounded-full mb-4`}>
            {isRejected ? (
              <AlertCircle className="h-10 w-10 text-red-600" />
            ) : (
              <Clock className="h-10 w-10 text-yellow-600" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isRejected ? 'Access Denied' : 'Approval Pending'}
          </h1>
          <p className="text-gray-600">
            {isRejected
              ? 'Your account request has been rejected by an administrator.'
              : 'Your account is awaiting administrator approval'}
          </p>
          {isRejected && user?.rejection_reason && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md">
              <p className="text-sm font-medium text-red-800">Reason:</p>
              <p className="text-sm text-red-700 italic">"{user.rejection_reason}"</p>
            </div>
          )}
        </div>

        <div className={`bg-gradient-to-r ${isRejected ? 'from-red-50 to-orange-50 border-red-200' : 'from-teal-50 to-cyan-50 border-teal-200'} border rounded-lg p-6 mb-8`}>
          <div className="flex items-start">
            <AlertCircle className={`h-5 w-5 ${isRejected ? 'text-red-600' : 'text-teal-600'} mt-0.5 mr-3 flex-shrink-0`} />
            <div>
              <h3 className={`font-semibold ${isRejected ? 'text-red-900' : 'text-teal-900'} mb-2`}>
                {isRejected ? 'What can you do?' : 'What happens next?'}
              </h3>
              <p className={`text-sm ${isRejected ? 'text-red-800' : 'text-teal-800'}`}>
                {isRejected ? (
                  <>
                    If you believe this was a mistake, you can request access again for review.
                    Please ensure your GitHub profile is up to date.
                  </>
                ) : (
                  <>
                    An administrator will review your account and approve access to the Files Hub.
                    You'll receive notification once approved. This typically takes 1-2 business days.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {user && (
          <div className="space-y-4 mb-8">
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <img
                src={user.avatar_url}
                alt={user.name}
                className="w-12 h-12 rounded-full mr-4"
              />
              <div>
                <h3 className="font-semibold text-gray-900">{user.name || user.username}</h3>
                <p className="text-sm text-gray-600">@{user.username}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {isRejected && (
            <Button
              onClick={handleReRequest}
              disabled={requesting}
              className="w-full py-6 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-md transition-all active:scale-[0.98]"
            >
              {requesting ? 'Processing...' : 'Re-request Access'}
            </Button>
          )}
          <Button
            onClick={logout}
            variant="outline"
            className="w-full py-6"
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ApprovalPendingPage;
